/**
 * Admin — Access Control
 *
 * Platform-level trust & safety controls:
 *   1. Blocked Share Domains — URL blacklist for community feed sharing
 *   (Future sections: email whitelist/blacklist, invitation codes, OAuth config, rate limits)
 *
 * English only (admin UI per CLAUDE.md).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchBlockedDomains,
  addBlockedDomain,
  removeBlockedDomain,
  resolveBlockedDomainAppeal,
  forceReblockDomain,
  BlockedShareDomain,
} from '../../api/newsApi';
import styles from './AdminPages.module.css';

export default function AdminAccessControl() {
  // ── Blocked Domains state ──────────────────────────────
  const [blockedDomains, setBlockedDomains] = useState<BlockedShareDomain[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [newDomainReason, setNewDomainReason] = useState('');

  // ── General state ──────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const statusTimer = useRef<ReturnType<typeof setTimeout>>();

  // ── Load on mount ──────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const domains = await fetchBlockedDomains();
        if (!cancelled) setBlockedDomains(domains);
      } catch {
        // Backend not ready
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Flash status ───────────────────────────────────────
  const flash = useCallback((type: 'success' | 'error', msg: string) => {
    setStatus({ type, msg });
    if (statusTimer.current) clearTimeout(statusTimer.current);
    statusTimer.current = setTimeout(() => setStatus(null), 4000);
  }, []);

  // ── Add blocked domain ────────────────────────────────
  const handleAddBlockedDomain = useCallback(async () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    if (blockedDomains.some((b) => b.domain.toLowerCase() === domain)) {
      flash('error', `"${domain}" is already blocked`);
      return;
    }
    try {
      const added = await addBlockedDomain(domain, newDomainReason.trim() || undefined);
      setBlockedDomains((prev) => [...prev, added]);
      setNewDomain('');
      setNewDomainReason('');
      flash('success', `Blocked "${domain}"`);
    } catch (err: any) {
      flash('error', `Failed to add domain: ${err.message || 'Unknown error'}`);
    }
  }, [newDomain, newDomainReason, blockedDomains, flash]);

  // ── Remove blocked domain ─────────────────────────────
  const handleRemoveBlockedDomain = useCallback(async (domain: string) => {
    try {
      await removeBlockedDomain(domain);
      setBlockedDomains((prev) => prev.filter((b) => b.domain !== domain));
      flash('success', `Unblocked "${domain}"`);
    } catch (err: any) {
      flash('error', `Failed to remove domain: ${err.message || 'Unknown error'}`);
    }
  }, [flash]);

  // ── Resolve appeal (overturn / uphold) ──────────────
  const handleResolveAppeal = useCallback(async (domain: string, action: 'overturn' | 'uphold') => {
    const verb = action === 'overturn' ? 'Overturn block and clear' : 'Uphold block on';
    if (!window.confirm(`${verb} "${domain}"?`)) return;
    try {
      const updated = await resolveBlockedDomainAppeal(domain, { action });
      setBlockedDomains((prev) => prev.map((b) => b.domain === domain ? updated : b));
      flash('success', action === 'overturn'
        ? `Overturned block on "${domain}" — domain is now cleared.`
        : `Upheld block on "${domain}".`);
    } catch (err: any) {
      flash('error', `Failed to resolve appeal: ${err.message || 'Unknown error'}`);
    }
  }, [flash]);

  // ── Force re-block an overturned domain ─────────────
  const handleForceReblock = useCallback(async (domain: string) => {
    const reason = window.prompt(`Re-block "${domain}" — reason (optional):`);
    if (reason === null) return; // cancelled
    try {
      const updated = await forceReblockDomain(domain, reason.trim() || undefined);
      setBlockedDomains((prev) => prev.map((b) => b.domain === domain ? updated : b));
      flash('success', `Re-blocked "${domain}" (system admin override).`);
    } catch (err: any) {
      flash('error', `Failed to re-block: ${err.message || 'Unknown error'}`);
    }
  }, [flash]);

  // ── Render ─────────────────────────────────────────────
  if (loading) {
    return (
      <div>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>Access Control</h1>
            <p className={styles.pageSubtitle}>Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Access Control</h1>
          <p className={styles.pageSubtitle}>
            Platform-level trust &amp; safety — manage blocked domains, access rules, and rate limits.
          </p>
        </div>
      </div>

      {status && (
        <div className={`${styles.statusBar} ${status.type === 'success' ? styles.status_success : styles.status_error}`}>
          {status.msg}
        </div>
      )}

      {/* ═══ Section 1: Blocked Share Domains ═══ */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Blocked Share Domains</h2>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            {blockedDomains.filter((b) => b.isActive).length} active
          </span>
        </div>
        <p className={styles.sectionHint}>
          Domains on this list are rejected when users share links in community feeds. Use this to block URL shorteners, IP loggers, spam sites, and any other domains that should not appear in feeds. Both the exact domain and its parent domain are checked (e.g., blocking <code>example.com</code> also blocks <code>sub.example.com</code>).
        </p>

        {/* Add domain form */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'flex-end' }}>
          <label className={styles.fieldLabel} style={{ flex: 1, marginBottom: 0 }}>
            Domain
            <input
              className={styles.input}
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddBlockedDomain()}
              placeholder="e.g., spam-site.com"
            />
          </label>
          <label className={styles.fieldLabel} style={{ flex: 1, marginBottom: 0 }}>
            Reason (optional)
            <input
              className={styles.input}
              type="text"
              value={newDomainReason}
              onChange={(e) => setNewDomainReason(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddBlockedDomain()}
              placeholder="e.g., URL shortener — hides destination"
            />
          </label>
          <button
            className={styles.btnAdd}
            onClick={handleAddBlockedDomain}
            disabled={!newDomain.trim()}
            style={{ whiteSpace: 'nowrap', marginBottom: 0 }}
          >
            + Block Domain
          </button>
        </div>

        {/* Blocked domains table */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', textAlign: 'start' }}>
                <th style={{ padding: '8px 12px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Domain</th>
                <th style={{ padding: '8px 12px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Reason</th>
                <th style={{ padding: '8px 12px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '8px 12px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', textAlign: 'center' }}>Reports</th>
                <th style={{ padding: '8px 12px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Added</th>
                <th style={{ padding: '8px 12px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blockedDomains.map((b) => {
                const appealBadge = (() => {
                  switch (b.appealStatus) {
                    case 'pending':
                      return { label: 'Appeal Pending', bg: '#fef3c7', color: '#92400e' };
                    case 'upheld':
                      return { label: 'Upheld', bg: '#fee2e2', color: '#991b1b' };
                    case 'overturned':
                      return { label: 'Overturned', bg: '#d1fae5', color: '#065f46' };
                    default:
                      return b.isActive
                        ? { label: 'Active', bg: '#fee2e2', color: '#991b1b' }
                        : { label: 'Inactive', bg: '#f3f4f6', color: '#6b7280' };
                  }
                })();

                return (
                  <tr key={b.id} style={{ borderTop: '1px solid #f3f4f6', opacity: b.appealStatus === 'overturned' ? 0.7 : 1 }}>
                    <td style={{ padding: '6px 12px', fontWeight: 500, color: '#1a1a2e', fontFamily: 'monospace', fontSize: 12 }}>
                      {b.domain}
                    </td>
                    <td style={{ padding: '6px 12px', color: '#6b7280' }}>
                      {b.reason || '\u2014'}
                    </td>
                    <td style={{ padding: '6px 12px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 10,
                        fontSize: 11,
                        fontWeight: 600,
                        background: appealBadge.bg,
                        color: appealBadge.color,
                      }}>
                        {appealBadge.label}
                      </span>
                    </td>
                    <td style={{ padding: '6px 12px', textAlign: 'center', color: '#6b7280', fontSize: 12 }}>
                      {b.blockCount > 1 ? `${b.blockCount}x` : '1'}
                    </td>
                    <td style={{ padding: '6px 12px', color: '#9ca3af', fontSize: 11 }}>
                      {b.createdAt ? new Date(b.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '\u2014'}
                    </td>
                    <td style={{ padding: '6px 12px', textAlign: 'center', display: 'flex', gap: 4, justifyContent: 'center' }}>
                      {/* Appeal pending: show Overturn / Uphold */}
                      {b.appealStatus === 'pending' && (
                        <>
                          <button
                            onClick={() => handleResolveAppeal(b.domain, 'overturn')}
                            title="Overturn — clear the block"
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #059669', background: '#ecfdf5', color: '#059669', cursor: 'pointer', fontWeight: 600 }}
                          >
                            Overturn
                          </button>
                          <button
                            onClick={() => handleResolveAppeal(b.domain, 'uphold')}
                            title="Uphold — keep the block"
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #dc2626', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}
                          >
                            Uphold
                          </button>
                        </>
                      )}
                      {/* Overturned: system admin can force re-block */}
                      {b.appealStatus === 'overturned' && (
                        <button
                          onClick={() => handleForceReblock(b.domain)}
                          title="Force re-block (system admin override)"
                          style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #dc2626', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}
                        >
                          Re-block
                        </button>
                      )}
                      {/* Active domains (no pending appeal): standard remove */}
                      {b.isActive && b.appealStatus !== 'pending' && (
                        <button
                          className={styles.btnRemove}
                          onClick={() => handleRemoveBlockedDomain(b.domain)}
                          title={`Unblock ${b.domain}`}
                          style={{ marginTop: 0 }}
                        >
                          \u2715
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {blockedDomains.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontStyle: 'italic' }}>
                    No blocked domains. Users can share links from any domain.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ Future sections (placeholder) ═══ */}
      <div className={styles.placeholderBox}>
        <div className={styles.placeholderIcon}>⊗</div>
        <p className={styles.placeholderText}>Additional access controls coming soon</p>
        <div className={styles.placeholderFeatures}>
          Planned for this panel:
          <ul>
            <li>Email whitelist / blacklist management</li>
            <li>Invitation code generation</li>
            <li>OAuth provider configuration</li>
            <li>Rate limiting settings</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
